import { useState, type FormEvent } from 'react';
import { formatMoney } from '@vns/core';
import { useLookups, todayLocal } from '../lib/lookups';
import {
  useCreateExpenseBatch,
  emptyExpenseLine,
  filledExpenseCount,
  expensesTotalPaise,
  expenseLineTotalPaise,
  validateExpense,
  expenseHasErrors,
  type ExpenseDraft,
  type ExpenseLine,
  type ExpenseErrors,
} from '../lib/expense-batches';
import { Drawer, Field, useToast, Select } from './ui';
import { Wallet, Receipt, Plus, Trash2, Calendar, DollarSign, Sparkles } from 'lucide-react';

/**
 * Record site expenses — petty cash spend, driver bata, fuel, transport, and site sundries.
 */
export default function ExpenseDrawer({
  projectId,
  onClose,
}: {
  projectId: string;
  onClose: () => void;
}) {
  const { data: lk, isLoading } = useLookups();
  const create = useCreateExpenseBatch(projectId);
  const toast = useToast();

  const [draft, setDraft] = useState<ExpenseDraft>({
    date: todayLocal(),
    bill_no: '',
    default_paid_to: '',
    remarks: '',
    lines: [emptyExpenseLine()],
  });
  const [errors, setErrors] = useState<ExpenseErrors>({ lines: {} });
  const [touched, setTouched] = useState(false);

  const set = <K extends keyof ExpenseDraft>(k: K, v: ExpenseDraft[K]) => {
    const next = { ...draft, [k]: v };
    setDraft(next);
    if (touched) setErrors(validateExpense(next));
  };

  const patchLine = (i: number, patch: Partial<ExpenseLine>) => {
    const next: ExpenseDraft = {
      ...draft,
      lines: draft.lines.map((l, n) => (n === i ? { ...l, ...patch } : l)),
    };
    setDraft(next);
    if (touched) setErrors(validateExpense(next));
  };

  const addLine = () =>
    setDraft((d) => ({ ...d, lines: [...d.lines, emptyExpenseLine()] }));

  const removeLine = (i: number) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.length > 1 ? d.lines.filter((_, n) => n !== i) : [emptyExpenseLine()],
    }));

  const count = filledExpenseCount(draft.lines);
  const total = expensesTotalPaise(draft.lines);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    const found = validateExpense(draft);
    setErrors(found);
    if (expenseHasErrors(found)) return;

    try {
      await create.mutateAsync(draft);
      toast.show(`Expense recorded — ${count} item${count === 1 ? '' : 's'}`);
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the expense', true);
    }
  }

  return (
    <Drawer
      title="Add Site Expense"
      description="Record petty cash spend, transport, fuel, refreshments, and site sundries"
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Save Expense Entry"
      busy={create.isPending}
      wide
    >
      <div className="space-y-4">
        {/* Top Header Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50/70 p-3.5 sm:p-4 rounded-[10px] border border-slate-200/80">
          <Field label="Bill or Invoice Date" error={errors.date}>
            <input
              type="date"
              value={draft.date}
              onChange={(e) => set('date', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>

          <Field label="Bill / Voucher No." optional hint="Applied across all items">
            <input
              value={draft.bill_no}
              maxLength={40}
              onChange={(e) => set('bill_no', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
            />
          </Field>

          <Field label="Default Paid To" optional hint="Recipient for these items">
            <input
              value={draft.default_paid_to}
              onChange={(e) => set('default_paid_to', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-white border border-slate-200/80 rounded-[8px] focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>
        </div>

        {/* Multi-Row Expense Lines Table */}
        <div className="bg-white rounded-[10px] border border-slate-200/80 p-3.5 sm:p-4 space-y-3 shadow-xs">
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-slate-800 m-0 flex items-center gap-1.5">
                <Receipt className="w-4 h-4 text-amber-600" />
                Expenses by Category
              </h4>
              <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                Categorize each site voucher or petty cash expense item.
              </p>
            </div>
            {errors.form && (
              <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2.5 py-0.5 rounded-[6px] border border-rose-200">
                {errors.form}
              </span>
            )}
          </div>

          <div className="bill-panel p-0 bg-transparent border-0">
            <div className="expense-line-head hidden sm:grid text-[11px] text-slate-400 font-bold uppercase tracking-wider pb-2 border-b border-slate-200/80">
              <div>Expense Category</div>
              <div>Name / Purpose of Expense</div>
              <div className="text-center">Amount (₹)</div>
              <div>Paid To (Optional)</div>
              <div />
            </div>

            <div className="bill-lines space-y-2 pt-2">
              {draft.lines.map((line, i) => {
                const le = errors.lines[i] ?? {};
                return (
                  <div
                    key={i}
                    className="expense-line items-center gap-2 bg-slate-50/50 p-2 rounded-[8px] border border-slate-200/60 hover:bg-slate-50 transition-colors"
                  >
                    <div className="lineno text-xs font-bold text-slate-400">#{i + 1}</div>

                    {/* Category Selector */}
                    <div className="field category">
                      <Select
                        size="sm"
                        value={line.category_id}
                        disabled={isLoading}
                        placeholder={isLoading ? 'Loading…' : 'Choose category'}
                        options={[
                          { value: '', label: isLoading ? 'Loading…' : 'Choose category' },
                          ...(lk?.expenseCategories?.map((c) => ({ value: c.id, label: c.name })) ?? []),
                          { value: '__other__', label: 'Others (Specify below)…' },
                        ]}
                        onChange={(val) => patchLine(i, { category_id: val })}
                      />

                      {line.category_id === '__other__' && (
                        <input
                          value={line.custom_category ?? ''}
                          onChange={(e) => patchLine(i, { custom_category: e.target.value })}
                          className="w-full px-2 py-1 text-xs bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 mt-1"
                        />
                      )}
                    </div>

                    {/* Purpose / Title */}
                    <div className="field title">
                      <input
                        id={`t-${i}`}
                        value={line.title}
                        aria-invalid={!!le.title}
                        onChange={(e) => patchLine(i, { title: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800"
                      />
                      {le.title && <div className="text-[10px] text-rose-600 mt-0.5">{le.title}</div>}
                    </div>

                    {/* Amount */}
                    <div className="field amount-input">
                      <input
                        id={`a-${i}`}
                        value={line.amountRupees}
                        inputMode="decimal"
                        aria-invalid={!!le.amountRupees}
                        onChange={(e) => patchLine(i, { amountRupees: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs text-center bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800 font-mono font-semibold"
                      />
                      {le.amountRupees && <div className="text-[10px] text-rose-600 mt-0.5">{le.amountRupees}</div>}
                    </div>

                    {/* Paid To */}
                    <div className="field paidto">
                      <input
                        id={`pt-${i}`}
                        value={line.paid_to}
                        onChange={(e) => patchLine(i, { paid_to: e.target.value })}
                        className="w-full px-2.5 py-1.5 text-xs bg-white border border-slate-200/80 rounded-[6px] focus:outline-none focus:ring-1 focus:ring-sky-500 text-slate-800"
                      />
                    </div>

                    {/* Remove Line */}
                    <button
                      type="button"
                      onClick={() => removeLine(i)}
                      title="Remove this expense line"
                      aria-label={`Remove line ${i + 1}`}
                      className="btn-remove-line rm"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={addLine}
              className="btn-add-line"
            >
              <Plus size={14} strokeWidth={2.2} />
              <span>Add Another Expense Line</span>
            </button>
          </div>
        </div>

        {/* Totals Summary Banner */}
        <div className="drawer-summary-banner">
          <span className="title">
            {count} expense{count === 1 ? '' : 's'} entered
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>Total Spend:</span>
            <span className="amount" style={{ color: '#ea580c' }}>
              {formatMoney(total)}
            </span>
          </div>
        </div>

        {/* General Remarks */}
        <div>
          <Field label="Expense Remarks" optional span2>
            <textarea
              rows={2}
              value={draft.remarks}
              maxLength={300}
              onChange={(e) => set('remarks', e.target.value)}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>
        </div>
      </div>
    </Drawer>
  );
}
